import React from 'react';
import styled, { css } from 'styled-components/macro';

import stringToSlug from 'helpers/stringToSlug';
import modularScale from 'styles/helpers/modular-scale';
import ActiveFilters from 'components/base/ActiveFilters/ActiveFilters';
import ToolbarOptions from 'components/utility/GenericComponent/ToolbarOptions/ToolbarOptions';

const Container = styled('div')<any>`
  color: ${({ theme }): string => theme.NightBlue};

  ${({ isMobileView }): string =>
    isMobileView
      ? `
  margin-left: 1em;`
      : ''}

  &:not(:first-of-type) {
    border-top: 1px solid ${({ theme }): string => theme.LightGrey};
    margin-top: ${({ theme }): string => theme.spacing.large};
    padding-top: 1em;
  }
`;

export const Title = styled('h2')<any>`
  font-size: ${({ isMobileView, newDesign }): string =>
    isMobileView ? '20px' : newDesign ? '24px' : modularScale(2)};
  color: inherit;
  font-weight: ${({ titleAlignLeft }): number => (titleAlignLeft ? 700 : 600)};
  position: relative;
  margin-bottom: 0;
  margin-top: ${({ theme, isMobileView }): string =>
    isMobileView ? '0' : theme.spacing.xxsmall};

  svg {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: -${({ theme, titleAlignLeft, alignSVG }): string => (titleAlignLeft ? alignSVG || '1em' : theme.spacing.xlarge)};
  }

  @media screen and (min-width: 50em) {
    margin-top: ${({ theme }): string => theme.spacing.xsmall};
  }
`;

export const Header = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-left: ${({ theme }): string => theme.spacing.xxlarge};

  @media screen and (min-width: 50em) {
    padding-left: 0;
  }
`;

const ComponentFilters = styled(ActiveFilters)`
  margin: 0 0 ${({ theme }): string => theme.spacing.xxsmall};
  color: ${({ theme }): string => theme.DeepBlue};
  font-size: ${modularScale(-1)};
`;

export const StyledToolbarOptions = styled(ToolbarOptions)`
  position: relative;

  @media screen and (min-width: 50em) {
    margin-right: -4.2em;
    margin-top: 0.5em;
  }
`;

const FiltersWrapper = styled('div')<any>`
  ${({ newDesign }): string =>
    newDesign ? `font-size: 13.2px; color: #000;` : ''};
  ${({ titleAlignLeft }): string =>
    titleAlignLeft && typeof titleAlignLeft !== 'string'
      ? 'margin-left: -1.6em;'
      : ''}
  ${({ isMobileView }): string =>
    isMobileView
      ? `
      font-size: 13.2px;
      margin-top: 0.2em;
    `
      : ''}
  ${({ filterMarginLeft }): string =>
    filterMarginLeft
      ? `
      margin-left: ${filterMarginLeft};
    `
      : ''}
`;
const TitleWrapper = styled('div')<any>`
  ${({ titleAlignLeft }): string =>
    titleAlignLeft
      ? `margin-left: ${
          typeof titleAlignLeft === 'string' ? titleAlignLeft : '-2.5em'
        };`
      : ''}
`;

export const ToolbarOptionsWrapper = styled('div')<any>`
  margin-left: auto;
  flex-shrink: 0;
  position: relative;
`;

export const PlaylistKPIWrapper = styled('div')`
  margin-top: 46px;
  @media screen and (min-width: 50em) {
    ${css`
      padding-left: ${({ theme }): string =>
        `calc(${theme.spacing.xlarge} * 1.99)`};
    `}
  }
`;

/** KPIList wrapper component that fetches the data for a specific KPIList.
 * Furthermore it shows a header with title, icon and ToolbarDropdown.
 */

/**
 * KPIWrapper is a container element that handles layout and displays an icon and title as well as a dropdown with a glossary link and possibly other items.
 *
 * @param props React props
 * @param props.title The title for these KPI's
 * @param props.icon The SVG icon for these KPI's
 * @param props.allowedFilters The allowed customer and country filters
 * @param props.glossarySlug The slug pointing to the relevant glossary entry
 * @param props.children React children
 * @param props.extraToolbarItems Extra items to display in the toolbar, look at Toolbar for the shape.
 * @returns
 */

interface IKPIWrapper {
  title: string;
  icon?: any;
  children: any;
  glossarySlug?: string;
  extraToolbarItems?: {
    label: string;
    href: string;
    type: string;
    order: number;
  }[];
  allowedFilters?: string[];
  titleAlignLeft?: string | boolean;
  isMobileView?: boolean;
  newDesign?: boolean;
  alignSVG?: string;
  iconWidth?: number;
  iconHeight?: number;
  filterMarginLeft?: string;
}

const KPIWrapper: React.FC<IKPIWrapper> = (props) => {
  const {
    title,
    icon: Icon,
    allowedFilters,
    glossarySlug,
    children,
    extraToolbarItems,
    titleAlignLeft,
    isMobileView,
    newDesign,
    alignSVG,
    iconWidth,
    iconHeight,
    filterMarginLeft,
  } = props;

  return (
    <Container data-pw={stringToSlug(title)} isMobileView={isMobileView}>
      <Header>
        <TitleWrapper titleAlignLeft={titleAlignLeft}>
          <Title
            titleAlignLeft={titleAlignLeft}
            isMobileView={isMobileView}
            alignSVG={alignSVG}
            newDesign={newDesign}
          >
            {Icon ? (
              isMobileView ? (
                <Icon width={iconWidth || 13} height={iconHeight || 15} />
              ) : (
                <Icon />
              )
            ) : null}
            {title}
          </Title>
          <FiltersWrapper
            titleAlignLeft={titleAlignLeft}
            isMobileView={isMobileView}
            newDesign={newDesign}
            filterMarginLeft={filterMarginLeft}
          >
            <ComponentFilters
              allowedFilters={allowedFilters}
              isMobileView={isMobileView}
            />
          </FiltersWrapper>
        </TitleWrapper>
        {extraToolbarItems && (
          <ToolbarOptionsWrapper>
            <StyledToolbarOptions
              glossarySlug={glossarySlug}
              extraToolbarItems={extraToolbarItems}
              title={title}
            />
          </ToolbarOptionsWrapper>
        )}
      </Header>
      {children}
    </Container>
  );
};

export default KPIWrapper;
